import { useCallback, useEffect, useRef, useState } from 'react';
import type { WhatsAppOnboardingConfigView, WhatsAppOnboardingStartView, WhatsAppOnboardingView } from '@hilo/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { adminKeys, fetchAdminConnections } from '../../lib/admin';
import { api } from '../../lib/api';
import { launchWhatsAppSignup, loadWhatsAppSDK, readWhatsAppFinish } from '../../lib/whatsapp-signup';
import { useSensitiveAction } from '../auth/ReauthDialog';
import { errorText } from '../../lib/copy';
import { useCurrentUser } from '../../lib/session';
import { ChannelMark } from '../../ui/ChannelMark';
import { IconBack, IconCheck } from '../../ui/icons';
import { AdminShell } from './AdminShell';
import common from './Connections.module.css';
import styles from './WhatsAppOnboarding.module.css';

/** Official Meta Business app onboarding; authorization and phone selection are verified by the API. */
export function WhatsAppOnboardingPage() {
  const user = useCurrentUser();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const id = params.get('alta');
  const configuration = useQuery({ queryKey: ['admin', 'whatsapp-config'], queryFn: () => api<WhatsAppOnboardingConfigView>('GET', '/onboarding/whatsapp/config'), enabled: user.role === 'ADMIN' });
  const onboarding = useQuery({ queryKey: ['admin', 'whatsapp-onboarding', id], queryFn: () => api<WhatsAppOnboardingView>('GET', '/onboarding/whatsapp/' + id),
    enabled: user.role === 'ADMIN' && Boolean(id && /^[0-9a-f-]{36}$/.test(id)) });
  const [prepared, setPrepared] = useState<WhatsAppOnboardingStartView | null>(null);
  const [busy, setBusy] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { perform, dialog } = useSensitiveAction();
  const pending = useRef<{ state: string; code?: string; assets?: { wabaId: string; phoneNumberId?: string }; submitted: boolean } | null>(null);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; pending.current = null; };
  }, []);
  useEffect(() => {
    if (!waiting) return;
    const timer = window.setTimeout(() => {
      pending.current = null; setWaiting(false); setPrepared(null);
      setError('No recibimos la confirmación de Meta. Vuelve a empezar la conexión.');
    }, 10 * 60000);
    return () => window.clearTimeout(timer);
  }, [waiting]);
  const exchange = useCallback(async () => {
    const current = pending.current;
    if (!current || current.submitted || !current.code || !current.assets) return;
    current.submitted = true; setBusy(true);
    try {
      const result = await api<WhatsAppOnboardingView>('POST', '/onboarding/whatsapp/authorize', {
        body: { state: current.state, code: current.code, ...current.assets },
      });
      if (!mounted.current || pending.current !== current) return;
      setParams({ alta: result.id }, { replace: true });
    } catch (failure) { if (mounted.current) setError(errorText(failure)); }
    finally { if (mounted.current) { setBusy(false); setWaiting(false); setPrepared(null); pending.current = null; } }
  }, [setParams]);
  useEffect(() => {
    const receive = (event: MessageEvent) => {
      const assets = readWhatsAppFinish(event);
      if (!assets || !pending.current || pending.current.submitted) return;
      pending.current.assets = assets; void exchange();
    };
    window.addEventListener('message', receive);
    return () => window.removeEventListener('message', receive);
  }, [exchange]);
  const prepare = async () => {
    if (!configuration.data?.enabled) return;
    setBusy(true); setError(null);
    try {
      await loadWhatsAppSDK(configuration.data);
      const result = await perform(() => api<WhatsAppOnboardingStartView>('POST', '/onboarding/whatsapp/start'));
      if (result && mounted.current) setPrepared(result);
    } catch (failure) { setError(errorText(failure)); }
    finally { if (mounted.current) setBusy(false); }
  };
  const launch = () => {
    if (!prepared || !configuration.data?.enabled || busy || waiting) return;
    if (new Date(prepared.expiresAt).getTime() <= Date.now()) { setPrepared(null); setError('La preparación caducó. Vuelve a continuar con Meta.'); return; }
    const flow = { state: prepared.state, submitted: false };
    pending.current = flow; setWaiting(true); setError(null);
    try {
      launchWhatsAppSignup(configuration.data, code => {
        if (!mounted.current || pending.current !== flow) return;
        if (!code) { pending.current = null; setWaiting(false); setPrepared(null); setError('Cancelaste la conexión en Meta. No se añadió ningún número a Hilo.'); return; }
        pending.current.code = code; void exchange();
      });
    } catch (failure) { pending.current = null; setWaiting(false); setPrepared(null); setError(errorText(failure)); }
  };
  const connect = async (phoneNumberId: string) => {
    if (!id || busy) return;
    setBusy(true); setError(null);
    try {
      await api('POST', '/onboarding/whatsapp/' + id + '/connect', { body: { phoneNumberId } });
      setParams({}, { replace: true });
      await queryClient.invalidateQueries({ queryKey: adminKeys.connections });
      setError(null);
    } catch (failure) { setError(errorText(failure)); }
    finally { setBusy(false); }
  };
  const connections = useQuery({ queryKey: adminKeys.connections, queryFn: fetchAdminConnections, enabled: user.role === 'ADMIN' });
  const numbers = (connections.data ?? []).filter(connection => connection.channel === 'WHATSAPP')
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'es'));
  return (
    <AdminShell>
      <header className={common.header}>
        <div>
          <p className={common.eyebrow}>Conexiones · WhatsApp</p>
          <h1 className={common.title}>Conectar WhatsApp</h1>
          <p className={common.lead}>Añade los números que atiende tu equipo. Cada uno tiene sus propias conversaciones y personas autorizadas.</p>
        </div>
        <Link to="/conexiones" className="btn btn-secondary"><IconBack />Volver a Conexiones</Link>
      </header>
      <section className={styles.setup} aria-labelledby="conservar-numero">
        <div className={styles.intro}>
          <span className={styles.channel} aria-hidden="true"><ChannelMark channel="WHATSAPP" /></span>
          <h2 id="conservar-numero" className={common.sectionTitle}>Usa el número de WhatsApp Business</h2>
          <p className={common.sectionLead}>La conexión oficial permite atender desde Hilo y seguir usando el mismo número en la app WhatsApp Business del teléfono.</p>
          <ul className={styles.requirements}>
            <li><IconCheck />Ten a mano el teléfono con WhatsApp Business actualizado.</li>
            <li><IconCheck />Usa una cuenta con acceso al negocio en Meta.</li>
            <li><IconCheck />Autoriza cada número que quieras añadir.</li>
          </ul>
        </div>
        <div className={styles.next}>
          <h3 className={common.sectionTitle}>Cómo se conecta</h3>
          <ol className={styles.steps}>
            <li>Elige tu negocio y número en el formulario oficial de Meta.</li>
            <li>Confirma la conexión en el teléfono con el código o QR que Meta te muestre.</li>
            <li>Al volver a Hilo, asigna quién atenderá esa cuenta.</li>
          </ol>
          <div className={styles.availability} role="status" id="whatsapp-disponibilidad">
            <strong>{configuration.data?.enabled ? 'Conexión oficial con Meta' : 'Estamos habilitando esta conexión'}</strong>
            <p>{configuration.data?.enabled ? 'Cada número se añade en modo de pruebas. No importamos el historial de chats del teléfono.' : 'El alta de nuevos números todavía no está habilitada en Hilo.'}</p>
          </div>
          {configuration.isError && <p role="alert">{errorText(configuration.error)}</p>}
          {error && <p role="alert">{error}</p>}
          {waiting && <p role="status">Completa la autorización en Meta y confirma la conexión en tu teléfono…</p>}
          {prepared ? <button type="button" className="btn btn-primary" disabled={busy || waiting} onClick={launch}>Abrir formulario de Meta</button>
            : <button type="button" className="btn btn-primary" disabled={!configuration.data?.enabled || busy || waiting} onClick={() => void prepare()} aria-describedby="whatsapp-disponibilidad">{busy ? 'Preparando…' : 'Continuar con Meta'}</button>}
          {onboarding.isSuccess && onboarding.data.status === 'AUTHORIZED' && <div>
            <h3 className={common.sectionTitle}>Elige el número que quieres añadir</h3>
            <ul className={styles.requirements}>{onboarding.data.numbers.map(number => <li key={number.phoneNumberId}>
              <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => void connect(number.phoneNumberId)}>Añadir {number.name} · {number.displayPhoneNumber}</button>
            </li>)}</ul>
          </div>}
          {(onboarding.isError || (onboarding.isSuccess && onboarding.data.status !== 'AUTHORIZED')) && <p role="alert">Esta autorización ya no está disponible. Vuelve a continuar con Meta.</p>}
        </div>
      </section>
      <section className={common.section} aria-labelledby="cuentas-whatsapp">
        <div className={common.sectionHead}>
          <h2 id="cuentas-whatsapp" className={common.sectionTitle}>Cuentas de WhatsApp en Hilo</h2>
          <p className={common.sectionLead}>Puedes añadir más de un número. Las conversaciones de cada cuenta se mantienen separadas.</p>
        </div>
        {connections.isPending && <p className={common.muted} role="status">Cargando cuentas…</p>}
        {connections.isError && <div className={styles.loadError}>
          <p className={common.muted} role="alert">{errorText(connections.error)}</p>
          <button type="button" className="btn btn-secondary" onClick={() => void connections.refetch()}>Volver a intentar</button>
        </div>}
        {connections.isSuccess && numbers.length === 0 && <p className={styles.empty}>Todavía no hay cuentas de WhatsApp conectadas.</p>}
        {numbers.length > 0 && <ul className={styles.numbers}>
          {numbers.map(connection => <li key={connection.id} className={styles.number}>
            <ChannelMark channel="WHATSAPP" />
            <div className={styles.numberInfo}>
              <h3>{connection.displayName}</h3>
              <p>{!connection.enabled ? 'Cuenta desactivada' : !connection.credentialsPresent ? 'Requiere reconexión' : connection.mode === 'TEST' ? 'Solo destinatarios de prueba' : 'Atiende conversaciones reales'}</p>
            </div>
            <span className={styles.mode} data-mode={connection.mode}>{connection.mode === 'TEST' ? 'Pruebas' : 'Producción'}</span>
          </li>)}
        </ul>}
        <Link to="/conexiones" className={styles.manage}>Gestionar cuentas y personas autorizadas</Link>
      </section>
      {dialog}
    </AdminShell>
  );
}
